<template>
  <AnalyticsModuleContainer
    :title-key="config.titleKey"
    :grid-row-span="2"
    :explore-link="config.exploreLink"
    :benchmark-link="config.benchmarkLink"
    :info-content="config.infoContent"
    :member-short-titles="
      getMembersFromQuery({
        measures: [...firstQuery.measures, ...secondQuery.measures]
      })
    "
  >
    <template v-if="!error">
      <h3 class="subtitle">
        {{ $t(config.firstSubtitleKey) }}
      </h3>
      <div class="stats-container">
        <AnalyticsExtendedQueryRenderer
          :time-option="{ type: QueryTimeType.PERIOD_WITH_COMPARISON }"
          :query="firstQuery"
          @hide-module="hideModule"
        >
          <template #default="{ queryResults }">
            <AnalyticsRendererStats
              :query-results="queryResults"
              :icons="
                config.iconKeys && config.iconKeys.map((iconKey: string) => $t(iconKey))
              "
              :colors="config.iconKeys && colors"
            />
          </template>
        </AnalyticsExtendedQueryRenderer>
        <AnalyticsExtendedQueryRenderer
          :time-option="
            config.firstTrendType === TREND_TYPE.RATIO
              ? { type: QueryTimeType.PERIOD }
              : { type: QueryTimeType.PERIOD_WITH_TREND_GRAIN }
          "
          :query="firstQuery"
          class="stats-container__trend"
          @hide-module="hideModule"
        >
          <template #default="{ queryResults }">
            <AnalyticsRendererTrend
              :query-results="queryResults"
              :fill-missing-data="config.firstFillTrendMissingData"
              :trend-type="config.firstTrendType"
              :icons="
                config.iconKeys && config.iconKeys.map((iconKey: string) => $t(iconKey))
              "
            />
          </template>
        </AnalyticsExtendedQueryRenderer>
      </div>

      <h3 class="subtitle">
        {{ $t(config.secondSubtitleKey) }}
      </h3>
      <div class="stats-container">
        <AnalyticsExtendedQueryRenderer
          :time-option="{ type: QueryTimeType.PERIOD_WITH_COMPARISON }"
          :query="secondQuery"
          @hide-module="hideModule"
        >
          <template #default="{ queryResults }">
            <AnalyticsRendererStats
              :query-results="queryResults"
              :icons="
                config.iconKeys && config.iconKeys.map((iconKey: string) => $t(iconKey))
              "
              :colors="
                (config.iconKeys && colors) ||
                (config.secondHasSpecialColor && colors.slice(4, 5)) ||
                undefined
              "
            />
          </template>
        </AnalyticsExtendedQueryRenderer>
        <AnalyticsExtendedQueryRenderer
          :time-option="
            config.secondTrendType === TREND_TYPE.RATIO
              ? { type: QueryTimeType.PERIOD }
              : { type: QueryTimeType.PERIOD_WITH_TREND_GRAIN }
          "
          :query="secondQuery"
          class="stats-container__trend"
          @hide-module="hideModule"
        >
          <template #default="{ queryResults }">
            <AnalyticsRendererTrend
              :query-results="queryResults"
              :fill-missing-data="config.secondFillTrendMissingData"
              :trend-type="config.secondTrendType"
              :icons="
                config.iconKeys && config.iconKeys.map((iconKey: string) => $t(iconKey))
              "
            />
          </template>
        </AnalyticsExtendedQueryRenderer>
      </div>
    </template>
    <AnalyticsModuleErrorTemplate
      v-if="error"
      :error="error"
    />
  </AnalyticsModuleContainer>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import exportedColors from '~/assets/style/_exportDefaultChartColors.module.scss'
import { TREND_TYPE } from '~/constants/renderTypes'
import type { TranslationKeys } from '~/plugins/i18n'
import { getMembersFromQuery } from '~/services/analytics'
import { useAnalyticsStore } from '~/stores/analytics'
import {
  type ModuleExplorerLink,
  type ModuleInfoContent,
  type ModuleBenchmarkLink,
  QueryTimeType
} from '~/types/analytics'
import type { Executable, MinimalQuery } from '~/types/query'

type DoubleModuleConfig = {
  titleKey: TranslationKeys
  exploreLink?: ModuleExplorerLink
  benchmarkLink?: ModuleBenchmarkLink
  infoContent?: ModuleInfoContent
  iconKeys?: string[]
  firstSubtitleKey: TranslationKeys
  firstFillTrendMissingData?: boolean
  firstTrendType?: TREND_TYPE
  secondSubtitleKey: TranslationKeys
  secondFillTrendMissingData?: boolean
  secondHasSpecialColor?: boolean
  secondTrendType?: TREND_TYPE
}

export default {
  name: 'ModuleComponentsDouble',
  props: {
    firstQuery: {
      type: Object as PropType<Executable<MinimalQuery>>,
      required: true
    },
    secondQuery: {
      type: Object as PropType<Executable<MinimalQuery>>,
      required: true
    },
    config: {
      type: Object as PropType<DoubleModuleConfig>,
      required: true
    }
  },
  setup() {
    return {
      colors: Object.values(exportedColors),
      QueryTimeType,
      TREND_TYPE,
      getMembersFromQuery
    }
  },
  data(): { error: Error | undefined } {
    return {
      error: undefined
    }
  },
  computed: {
    ...mapState(useAnalyticsStore, ['analyticsParams'])
  },
  watch: {
    analyticsParams: {
      handler() {
        this.error = undefined
      }
    }
  },
  methods: {
    hideModule(error: any) {
      this.error = error
    }
  }
}
</script>

<style lang="scss" scoped>
.stats-container {
  display: flex;
  justify-content: space-between;

  &__trend {
    align-items: flex-end;
  }
}

.subtitle {
  display: flex;
  align-items: center;
  @include font-text($font-weight-medium);
  @include font-size($font-size-normal, 28px);
  color: $text-secondary;
  margin-top: $margin-regular;

  &:first-of-type {
    margin-top: $margin-mini;
  }
}
</style>
